import { Ticker, Container, Graphics } from "pixi.js";

// —— 补间与特效工具 ——
// Pixi v8 没有内置 tween，这里用共享 ticker 实现一个 promise 化的最小补间，
// 依赖仅 pixi.js 本身，不额外引第三方。

export type Easing = (t: number) => number;

export const linear: Easing = (t) => t;
export const easeOutCubic: Easing = (t) => 1 - Math.pow(1 - t, 3);
export const easeInOutQuad: Easing = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
export const easeOutBack: Easing = (t) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

export function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/** 在 duration 毫秒内，把归一化进度 t∈[0,1]（经缓动）喂给 onUpdate。 */
export function tween(opts: {
  duration: number;
  ease?: Easing;
  onUpdate: (v: number) => void;
  onComplete?: () => void;
}): Promise<void> {
  const { duration, ease = linear, onUpdate, onComplete } = opts;
  return new Promise((resolve) => {
    if (duration <= 0) {
      onUpdate(1);
      onComplete?.();
      resolve();
      return;
    }
    let elapsed = 0;
    const cb = (tk: Ticker) => {
      elapsed += tk.deltaMS;
      const t = Math.min(1, elapsed / duration);
      onUpdate(ease(t));
      if (t >= 1) {
        Ticker.shared.remove(cb);
        onComplete?.();
        resolve();
      }
    };
    Ticker.shared.add(cb);
  });
}

/** 消除光束：从 a 到 b 画一道会伸展、发光、再淡出的横杠。 */
export async function beam(
  layer: Container,
  a: { x: number; y: number },
  b: { x: number; y: number },
  color: number,
): Promise<void> {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  const angle = Math.atan2(dy, dx);
  const thickness = 6;

  const g = new Graphics();
  g.roundRect(0, -thickness / 2, len, thickness, thickness / 2).fill({ color, alpha: 0.95 });
  g.position.set(a.x, a.y);
  g.rotation = angle;
  g.pivot.set(0, 0);
  g.scale.set(0, 1);
  layer.addChild(g);

  await tween({
    duration: 160,
    ease: easeOutCubic,
    onUpdate: (t) => {
      g.scale.x = t * 1.05;
      g.alpha = Math.min(1, t * 2);
    },
  });
  await tween({
    duration: 220,
    ease: linear,
    onUpdate: (t) => {
      g.alpha = 1 - t;
    },
  });
  g.destroy();
}

/** 消除粒子爆发：在 center 处向四周飞散一圈色块。 */
export function burst(layer: Container, center: { x: number; y: number }, color: number, count = 10): void {
  for (let i = 0; i < count; i++) {
    const p = new Graphics();
    const r = 3 + Math.random() * 3;
    p.circle(0, 0, r).fill({ color, alpha: 0.9 });
    p.position.set(center.x, center.y);
    layer.addChild(p);

    const ang = (Math.PI * 2 * i) / count + Math.random() * 0.5;
    const dist = 24 + Math.random() * 28;
    const vx = Math.cos(ang) * dist;
    const vy = Math.sin(ang) * dist;

    void tween({
      duration: 420 + Math.random() * 160,
      ease: easeOutCubic,
      onUpdate: (t) => {
        p.x = center.x + vx * t;
        p.y = center.y + vy * t - 10 * t; // 轻微上飘
        p.alpha = 1 - t;
        p.scale.set(1 - t * 0.6);
      },
      onComplete: () => p.destroy(),
    });
  }
}
